import { axiosInstance } from "@/plugins/axios";

// Backend contract: pet_app.api.branch.get_branch_context
// See docs/backend/branch-selection.md and the build spec. The endpoint tells
// the client which clinic (Branch) a new scoped record must be filed into, and
// crucially whether the current user MUST be asked (`must_select`) because the
// backend can no longer infer it.
const BRANCH_CONTEXT_METHOD = "/api/method/pet_app.api.branch.get_branch_context";

/** One selectable clinic. `name` is the docname to submit; `branch` is the label. */
export interface BranchOption {
  name: string;
  branch: string;
  /**
   * `Branch.custom_wharehouse` — the Bin a counter sale at this branch is checked
   * against and relieved from.
   *
   * ⚠️ AUTHORITATIVE OVER `POS Profile.warehouse`, which can disagree and does on
   * this site: "Alkokh Vet Store - Main Cashier" advertises المخزن الرئيسي while its
   * branch (main) actually uses مخزن صالة العيادة. `stamp_branch_and_warehouse`
   * rewrites every invoice row to the branch's warehouse, so the profile's value is
   * a label, not a fact. `null` means the branch has no warehouse wired, and only
   * then does a sale fall back to the profile's.
   */
  warehouse: string | null;
}

export interface BranchContext {
  /** The user's own branch, or null when they are not pinned to exactly one. */
  current: string | null;
  /**
   * The CURRENT user's branch warehouse, or null.
   *
   * ⚠️ NOT NECESSARILY THE TILL'S. A till belongs to `POS Profile.branch`, which is
   * not always the user's own branch — and for an Administrator `current` is null
   * entirely. At a till, look the warehouse up from `available` by the profile's
   * branch; see `warehouseForBranch`.
   */
  warehouse: string | null;
  /** Branches the user may file into. */
  available: BranchOption[];
  /** true = the user is unrestricted (sees every branch). */
  seesAllBranches: boolean;
  /** true = the client MUST ask the user to choose (available > 1 && !current). */
  mustSelect: boolean;
}

function normalizeBranchOption(raw: any): BranchOption | null {
  const name = String(raw?.name ?? raw?.branch ?? "").trim();
  if (!name)
    return null;

  return {
    name,
    // The backend keeps `name` and `branch` as separate fields; do not assume
    // they are equal even though they currently are on this site.
    branch: String(raw?.branch ?? raw?.name ?? name).trim() || name,
    warehouse: raw?.warehouse ? String(raw.warehouse).trim() || null : null,
  };
}

function normalizeBranchContext(raw: any): BranchContext {
  const available = Array.isArray(raw?.available)
    ? raw.available
        .map(normalizeBranchOption)
        .filter((option: BranchOption | null): option is BranchOption => Boolean(option))
    : [];

  const current = raw?.current ? String(raw.current).trim() || null : null;

  return {
    current,
    warehouse: raw?.warehouse ? String(raw.warehouse).trim() || null : null,
    available,
    seesAllBranches: Boolean(raw?.sees_all_branches ?? raw?.seesAllBranches),
    // Trust the backend's computed flag, but fall back to the same rule it uses
    // (available > 1 && !current) if the field is ever absent.
    mustSelect:
      raw?.must_select ?? raw?.mustSelect ?? (available.length > 1 && !current),
  };
}

/**
 * Fetch the current user's branch context. The httpClient response interceptor
 * collapses a `{ message: { ok, data } }` envelope to `{ message: <data> }`, so
 * read `response.data.message` first, then fall back to the raw body for a
 * plain (un-enveloped) method response.
 */
export async function GetBranchContext(): Promise<BranchContext | null> {
  try {
    const response = await axiosInstance.get(BRANCH_CONTEXT_METHOD);
    const payload = response?.data?.message ?? response?.data ?? null;

    return payload ? normalizeBranchContext(payload) : null;
  }
  catch (error: any) {
    // A missing backend method must not break boot — the modal simply never
    // shows and creates behave exactly as they did before this feature.
    const status = error?.response?.status;
    const message = String(error?.message ?? "");
    const looksMissing =
      status === 404
      || /Not Found/i.test(message)
      || /ModuleNotFoundError|AttributeError/i.test(message);

    if (looksMissing) {
      console.info("Branch context backend is not installed yet.");
      return null;
    }

    console.warn("Failed to fetch branch context", error);
    return null;
  }
}
